import {
  Trash2,
  Database,
  Eye,
  FileCheck,
  Server,
  Key,
  Layers,
  Cpu,
  Globe,
  Lock,
  Search,
  MessageSquare,
  Download,
  BarChart3,
  AlertTriangle,
  Tag,
  ShieldAlert,
  Flag,
  BookOpen,
  GitBranch,
  GraduationCap,
  Activity,
  Share2,
  Volume2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const serverImg =
  "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwZGF0YSUyMGNlbnRlciUyMGNvcnBvcmF0ZXxlbnwxfHx8fDE3Nzc5MTUwMTB8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral";

export const privacyFeatures: {
  icon: LucideIcon;
  iconBg: string;
  title: string;
  subtitle: string;
  description: string;
  badges: string[];
}[] = [
  {
    icon: Trash2,
    iconBg: "bg-teal-600",
    title: "The Forget-Me Model",
    subtitle: "GDPR Right to Erasure",
    description:
      "Your organization can use admin-initiated purge workflows to support responses to GDPR-style erasure requests and similar subject-rights processes: authorized admins can permanently purge recordings and related metadata for a person or endpoint. Purge actions are recorded in the administrative audit trail.",
    badges: ["GDPR Art. 17", "Deletion audit trail", "Granular Control"],
  },
  {
    icon: Database,
    iconBg: "bg-emerald-600",
    title: "Total Sovereignty",
    subtitle: "Your Infrastructure, Your Rules",
    description:
      "Pro and Enterprise can archive segments to Google Workspace Drive before DeskReplay retention removes hot copies (your OAuth app, your Shared drive). Enterprise also supports primary BYOK storage: DeskReplay-hosted, your Cloudflare R2 bucket, or AWS S3 (one provider per organization). Archived evidence stays in your Drive; search metadata remains in DeskReplay.",
    badges: ["Pro: Drive archive", "Enterprise: R2 or S3 BYOK", "Google Workspace"],
  },
  {
    icon: Eye,
    iconBg: "bg-teal-600",
    title: "Non-Invasive Recording",
    subtitle: "Designed for HR & Legal",
    description:
      "We record work output, not keystrokes. DeskReplay captures visual screen output by default. Optional session audio is policy-gated: system PC output on Pro, system plus microphone on Enterprise. We never keylog, capture clipboard contents, or harvest biometrics.",
    badges: ["Screen-first", "Optional session audio", "No Keylogging"],
  },
  {
    icon: FileCheck,
    iconBg: "bg-emerald-600",
    title: "Immutable Audit Logs",
    subtitle: "Every Action Accounted For",
    description:
      "Administrative changes, deletion requests, and evidence access generate append-only audit records (who, what, when, source IP). Export the trail for regulators, HR, or counsel. Hash-chain verification is on the roadmap to further strengthen integrity guarantees.",
    badges: ["Append-only", "Exportable", "Integrity roadmap"],
  },
];

export const architectureFeatures: {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  bg: string;
  border: string;
}[] = [
  {
    icon: Server,
    title: "Self-Hosted Control",
    description: "Self-hosted backend for deployment on your own infrastructure. AWS, Azure, GCP, or on-premises.",
    color: "text-teal-400",
    bg: "bg-teal-500/10",
    border: "border-teal-500/20",
  },
  {
    icon: Key,
    title: "Enterprise Identity",
    description:
      "Bring your own IdP: SSO via standard OIDC and OAuth 2.0. Connect Microsoft Entra ID, Okta, Google Workspace, or other compatible providers your organization already operates. MFA follows your IdP policies. Enterprise Advanced RBAC adds role lanes plus optional per-user evidence scope (desktop sessions, CCTV, or both) so investigators see only the recording lanes they need.",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
  {
    icon: Layers,
    title: "Scalable Clients",
    description: "Organization-scoped logical clients for large endpoint fleets with clean data isolation between business units.",
    color: "text-teal-400",
    bg: "bg-teal-500/10",
    border: "border-teal-500/20",
  },
  {
    icon: Cpu,
    title: "Silent Rollout",
    description: "WinExe/WinService lightweight Windows agent. Fits enterprise quiet-deployment standards with group policy support.",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
  {
    icon: Globe,
    title: "Live streaming",
    description:
      "View sessions in the browser with low latency and encryption in transit. Streams terminate on infrastructure you control.",
    color: "text-teal-400",
    bg: "bg-teal-500/10",
    border: "border-teal-500/20",
  },
  {
    icon: Volume2,
    title: "Session audio streaming",
    description:
      "Optional AAC audio muxed into session segments when enabled. Pro: system loopback (meetings, softphone, PC output). Enterprise: microphone plus system, with stereo split or mixed mono for synchronized replay.",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
  {
    icon: Lock,
    title: "TLS Everywhere",
    description: "You control your own TLS certificates and domain names. End-to-end encryption from agent to console.",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
];

export const aiProFeatures: {
  icon: LucideIcon;
  iconBg: string;
  title: string;
  subtitle: string;
  description: string;
  badges: string[];
}[] = [
  {
    icon: Search,
    iconBg: "bg-violet-600",
    title: "Basic OCR",
    subtitle: "Find any text that appeared on screen, instantly",
    description:
      "Basic OCR runs at indexing time, extracting every text fragment visible on screen across your recordings. Search returns timestamped hits in seconds. Investigators jump directly to the exact moment text appeared with no footage scrubbing required.",
    badges: ["OCR Indexing", "Full-Text Search", "Timestamped Hits", "Pro"],
  },
  {
    icon: MessageSquare,
    iconBg: "bg-violet-600",
    title: "Chat with Your Recordings",
    subtitle: "Talk to your video archive - ask questions, get timestamped answers",
    description:
      "Natural language queries over your entire recording archive. The AI reads the OCR index and synthesizes answers that point directly to the recordings and timestamps relevant to your question. Follow up the same way you would in conversation.",
    badges: ["Natural Language", "Timestamped Answers", "Archive Q&A", "Pro"],
  },
];

export const aiEnterpriseFeatures: {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  bg: string;
  border: string;
}[] = [
  {
    icon: Download,
    title: "Forensic Evidence Export",
    description:
      "One-click evidence packages with chain of custody documentation. Court-ready bundles include recordings, metadata, and audit trail in a structured, verifiable format.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  {
    icon: BarChart3,
    title: "Activity Intelligence Reports",
    description:
      "Automated daily summaries of endpoint activity delivered to administrators. Spot behavioral trends across your fleet without reviewing individual recordings.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
  },
  {
    icon: AlertTriangle,
    title: "Behavioral Anomaly Alerts",
    description:
      "AI identifies unusual activity patterns across endpoints and raises alerts before incidents escalate. Configurable sensitivity with routing to your existing notification channels.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  {
    icon: Tag,
    title: "Smart Content Classification",
    description:
      "Automatic tagging for PII, risk indicators, and compliance-relevant content as recordings are indexed. Reduce manual review burden for compliance and legal teams.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
  },
  {
    icon: ShieldAlert,
    title: "Sensitive Data Detection",
    description:
      "Auto-detect personal and financial data visible on screen at indexing time. Flag recordings containing credit card numbers, government IDs, account numbers, and similar sensitive content.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  {
    icon: Flag,
    title: "Workplace Conduct Monitor",
    description:
      "AI flags recordings that may contain policy violations or conduct concerns. Surface potential incidents for HR and Legal review without manual footage screening.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
  },
  {
    icon: Eye,
    title: "Content Safety Screening",
    description:
      "Automated detection of inappropriate or explicit material across your recording archive. Supports acceptable use policy enforcement and regulatory content requirements.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  {
    icon: BookOpen,
    title: "Golden Path Learning",
    description:
      "AI learns your standard operating procedures from actual recorded behavior. Auto-generate process guidance and identify deviations from established workflows.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
  },
  {
    icon: GitBranch,
    title: "Data Journey Tracking",
    description:
      "Follow sensitive data across every endpoint that touched it. Build complete provenance trails for compliance investigations and data loss incidents.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  {
    icon: FileCheck,
    title: "Compliance Auto-Tagging",
    description:
      "Recordings automatically tagged by applicable regulation based on content and context: SOX, HIPAA, GDPR, and others. Accelerate audit preparation and eDiscovery.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
  },
  {
    icon: Activity,
    title: "Live AI Monitoring",
    description:
      "Real-time AI analysis across active endpoints with configurable alert thresholds. Instant notifications when defined conditions are detected, delivering visibility before incidents escalate.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  {
    icon: Share2,
    title: "Visual Process Discovery",
    description:
      "Auto-generate workflow diagrams from actual recorded behavior. Understand how work really happens versus documented procedures and surface process improvement opportunities.",
    color: "text-indigo-400",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
  },
  {
    icon: GraduationCap,
    title: "Shadow Training Mode",
    description:
      "Train new hires using anonymized recordings of top performers. AI-assisted playback highlights key decision points and process steps without exposing sensitive session data.",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
];

/** Short list for FAQ, pricing footnotes, and feature copy. */
export const supportedLlmProvidersBlurb =
  "OpenAI, Google Gemini, Claude (Anthropic), DeepSeek, Grok (xAI), Azure OpenAI, or self-hosted Ollama";

export const aiProviderOptions: {
  name: string;
  label: string;
  description: string;
  tier: string;
}[] = [
  {
    name: "OpenAI",
    label: "Cloud API (your credentials)",
    description:
      "Connect your OpenAI API key. Chat, narrative summaries, and text intelligence run under your account and OpenAI's data processing terms.",
    tier: "Pro and Enterprise",
  },
  {
    name: "Google Gemini",
    label: "Cloud API (your credentials)",
    description:
      "Use Gemini models with your Google AI or Vertex credentials. Same BYOK pattern: your keys, your provider agreement.",
    tier: "Pro and Enterprise",
  },
  {
    name: "Claude (Anthropic)",
    label: "Cloud API (your credentials)",
    description:
      "Route chat and summarization to Claude with your Anthropic API key. OCR text only; raw video never leaves your storage.",
    tier: "Pro and Enterprise",
  },
  {
    name: "DeepSeek",
    label: "Cloud API (your credentials)",
    description:
      "Cost-efficient reasoning and chat via your DeepSeek API credentials. Suitable for high-volume narrative and Q&A workloads.",
    tier: "Pro and Enterprise",
  },
  {
    name: "Grok (xAI)",
    label: "Cloud API (your credentials)",
    description:
      "Connect xAI Grok with your own API key for chat and text-based intelligence features.",
    tier: "Pro and Enterprise",
  },
  {
    name: "Azure OpenAI",
    label: "Tenant-controlled cloud AI",
    description:
      "Deploy models in your Azure subscription. Inference stays in your tenant and follows your organization's cloud governance.",
    tier: "Enterprise",
  },
  {
    name: "Ollama",
    label: "Self-hosted inference",
    description:
      "Run open models on your own hardware or private cloud. No external LLM API calls; suitable for air-gapped deployments.",
    tier: "Enterprise",
  },
];

export const complianceFrameworks = [
  { name: "GDPR", description: "Control alignment for Right to Erasure and data subject rights", region: "EU", supported: true },
  { name: "HIPAA", description: "Technical safeguard controls for PHI protection workflows", region: "US", supported: true },
  { name: "DPDP", description: "Control alignment for Digital Personal Data Protection Act", region: "India", supported: true },
  { name: "SOC 2", description: "Trust Services Criteria mapping (DeskReplay is not SOC 2 Type II certified)", region: "Global", supported: true },
  { name: "ISO 27001", description: "Information security control mapping (not a certified implementation)", region: "Global", supported: true },
  { name: "CCPA", description: "Control alignment for consumer rights and data deletion requests", region: "US", supported: true },
];

export type PlatformTopicSlug = "security" | "privacy" | "architecture" | "identity" | "deployment" | "ai-intelligence" | "cctv-ai-pipeline";

export const PLATFORM_TOPIC_SLUGS: PlatformTopicSlug[] = [
  "security",
  "privacy",
  "architecture",
  "identity",
  "deployment",
  "ai-intelligence",
  "cctv-ai-pipeline",
];

export const platformTopicCopy: Record<
  PlatformTopicSlug,
  { pageTitle: string; metaDescription: string; marketingCta: { titleBefore: string; titleAccent: string; description: string } }
> = {
  security: {
    pageTitle: "Security & trust architecture",
    metaDescription:
      "CISO-grade security: non-invasive capture, RBAC, TLS, append-only audit trail. Enterprise self-hosted keeps media inside your VPC; SaaS tiers use DeskReplay-operated hosting. GDPR, HIPAA, DPDP control mapping; forensic-ready evidence.",
    marketingCta: {
      titleBefore: "Ready to stress-test controls with ",
      titleAccent: "a CISO-minded team?",
      description:
        "Book a working session on threat model, retention, and identity. No generic slide deck: just how DeskReplay maps to your environment.",
    },
  },
  privacy: {
    pageTitle: "Privacy-by-design & data lifecycle",
    metaDescription:
      "Forget-me deletion, data sovereignty, non-invasive screen capture, and append-only audit trail. Privacy engineering for DPOs and legal teams.",
    marketingCta: {
      titleBefore: "Ready to align DPO, Legal, and IT on ",
      titleAccent: "one technical story?",
      description:
        "Walk through erasure, residency, and proportionality with engineers who built the model, not a checkbox checklist.",
    },
  },
  architecture: {
    pageTitle: "Platform components & architecture",
    metaDescription:
      "Self-hosted control plane, live streaming, organization-scoped clients, and enterprise identity (OIDC) for large endpoint fleets from desk-side to VPC topology.",
    marketingCta: {
      titleBefore: "Ready to see how the pieces snap into ",
      titleAccent: "your existing stack?",
      description:
        "We'll map agents, server, and console to your network zones, IdP, and change windows, so architecture review is concrete.",
    },
  },
  identity: {
    pageTitle: "Enterprise identity (SSO, OIDC, RBAC)",
    metaDescription:
      "Bring your own IdP: Microsoft Entra ID, Okta, Google Workspace, and OIDC/OAuth 2.0 SSO with role-based access for investigators and admins.",
    marketingCta: {
      titleBefore: "Ready to stop shipping ",
      titleAccent: "another password vault?",
      description:
        "Use the IdP you already run: OIDC, MFA, and least-privilege roles for everyone who can touch evidence.",
    },
  },
  deployment: {
    pageTitle: "Deployment topology & self-hosting",
    metaDescription:
      "Windows agents, encrypted streams to your backend, and investigator console in your domain. Deployment patterns for regulated and air-gapped networks.",
    marketingCta: {
      titleBefore: "Ready to draw your ",
      titleAccent: "first production cutover?",
      description:
        "We'll talk VPC placement, certificates, agent rollout, and how upgrades stay inside your change policy.",
    },
  },
  "ai-intelligence": {
    pageTitle: "AI Intelligence Layer",
    metaDescription:
      "Basic OCR on Pro, Advanced OCR and policy enforcement on Enterprise, plus natural language Q&A over your archive and enterprise AI features for compliance, behavioral analysis, and process discovery. All run on your infrastructure with your choice of AI provider.",
    marketingCta: {
      titleBefore: "Ready to get ",
      titleAccent: "answers in seconds, not hours?",
      description:
        "Walk through AI search, chat, and enterprise intelligence features with the team that built them. Bring your AI provider preferences and we'll map them to your data residency requirements.",
    },
  },
  "cctv-ai-pipeline": {
    pageTitle: "CCTV Dual-Engine AI Pipeline",
    metaDescription:
      "Hot path detection in under 2 seconds and cold path deep indexing in one unified archive. Real-time NVR stream alerts, visual search, OCR, NSFW screening, AI narrative summaries, all running on-premise with zero frames sent to third-party AI services.",
    marketingCta: {
      titleBefore: "Ready for AI that ",
      titleAccent: "works every shift, every camera?",
      description:
        "We will walk through NVR integration, hot path threshold configuration, and cold path indexing to show how the dual-engine pipeline fits your existing infrastructure.",
    },
  },
};
